import { getStaticsBaseUrl } from '@oysterjs/core/config';
import { CaraWorkspaceArtifactType } from '~/api/graphql';

export type DisplayArtifactType = CaraWorkspaceArtifactType;

/** Whether an artifact belongs in the Documents list; an EMAIL has no file to preview and is read and sent from the workspace composer. */
export const isDocumentArtifact = (type: CaraWorkspaceArtifactType): boolean =>
  type !== CaraWorkspaceArtifactType.Email;

/** Returns true for XLSX and DOCX artifacts that use the application-file URL. */
export const isApplicationFileArtifact = (type: CaraWorkspaceArtifactType): boolean =>
  type === CaraWorkspaceArtifactType.XlsxFile || type === CaraWorkspaceArtifactType.DocxFile;

/** Returns the file extension for download based on artifact type. */
export const getArtifactFileExtension = (type: CaraWorkspaceArtifactType): string => {
  switch (type) {
    case CaraWorkspaceArtifactType.DocumentHtml:
      return 'html';
    case CaraWorkspaceArtifactType.XlsxFile:
      return 'xlsx';
    case CaraWorkspaceArtifactType.DocxFile:
      return 'docx';
    default:
      return 'pdf';
  }
};

/** Builds the application-file URL for XLSX/DOCX artifacts. */
export const getApplicationFileUrl = (
  workspaceId: string,
  artifactId: string,
  version: number
): string =>
  `${getStaticsBaseUrl()}/agency/application-file/${workspaceId}/${artifactId}?version=${version}`;

/** The two Documents lists: active artifacts, and the ones archived out of it. */
export const documentsListPath = (archived: boolean): string =>
  archived ? '/app/documents/archived' : '/app/documents';

/** Preview slideout path under the list at `listPath`, which closing it returns to. */
export const documentPreviewPath = (
  listPath: string,
  workspaceId: string,
  artifactId: string,
  version: number
): string => `${listPath}/${workspaceId}/${artifactId}/${version}`;

export const getDisplayLabel = (type: CaraWorkspaceArtifactType): string => {
  switch (type) {
    case CaraWorkspaceArtifactType.DocumentHtml:
      return 'Document';
    case CaraWorkspaceArtifactType.PdfFile:
    case CaraWorkspaceArtifactType.CustomForm:
      return 'Custom PDF Form';
    case CaraWorkspaceArtifactType.TemplateForm:
      return 'ACORD Form';
    case CaraWorkspaceArtifactType.XlsxFile:
      return 'Excel File';
    case CaraWorkspaceArtifactType.DocxFile:
      return 'Word Document';
    default:
      return 'Unknown';
  }
};

export const getDaysSince = (date: Date): number => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

export const getDateRangeLabel = (diffDays: number): string => {
  if (diffDays <= 1) return 'Today';
  if (diffDays <= 2) return 'Yesterday';
  if (diffDays <= 7) return 'Past week';
  if (diffDays <= 30) return 'Past month';
  return 'Past year';
};

export const compareDates = (a: Date, b: Date): number => {
  return b.getTime() - a.getTime(); // Newest first
};

export const downloadFile = async (url: string, filename: string): Promise<void> => {
  try {
    // Fetch the file as a blob to ensure it downloads without opening a new tab
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to download file');
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback to direct download if fetch fails (e.g., CORS issues)
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

export const buildQueryString = (
  params: Record<string, string | number | null | undefined>
): string => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') {
      searchParams.append(key, String(value));
    }
  }
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

type ArtifactVersions = {
  versions: readonly { version: number; document?: { final?: unknown } | null }[];
};

/**
 * Why a version on the Documents tab cannot be edited there, if it cannot. Only the paged
 * document holds older versions still, as the workspace does.
 */
export const lockedVersion = (
  artifact: ArtifactVersions,
  version: number,
  pagedDocuments: boolean
): 'final' | 'older' | null => {
  const latest = Math.max(...artifact.versions.map((each) => each.version));
  if (pagedDocuments && version < latest) return 'older';
  return artifact.versions.find((each) => each.version === version)?.document?.final
    ? 'final'
    : null;
};
